import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdPhone, MdCallEnd, MdPerson } from 'react-icons/md';
import { socketService } from '../services';
import axiosInstance from '../services/axiosInstance';
import { useLanguage } from '../context/LanguageContext';
import { getRingtoneFile } from '../constants/ringtones';
import { ensureMicrophonePermission } from '../services/capacitorService';

// IndexedDB helpers — same DB as firebase-messaging-sw.js
const IDB_NAME  = 'teratask-sw';
const IDB_STORE = 'pending-call';
const IDB_KEY   = 'current';

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = (e: any) => e.target.result.createObjectStore(IDB_STORE);
    req.onsuccess = (e: any) => resolve(e.target.result);
    req.onerror   = (e: any) => reject(e.target.error);
  });
}

async function readPendingCall(): Promise<Record<string, string> | null> {
  try {
    const db = await openIDB();
    return new Promise((resolve, reject) => {
      const tx  = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = (e: any) => resolve(e.target.result ?? null);
      req.onerror   = (e: any) => reject(e.target.error);
    });
  } catch { return null; }
}

async function clearPendingCallIDB(): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror    = (e: any) => reject(e.target.error);
    });
  } catch { /* ignore */ }
}

// Programmatic beep — only used when no audio file is selected or file fails to load
function playBeepBurst(ctx: AudioContext) {
  const t = ctx.currentTime;
  const beep = (start: number, freq: number, dur: number) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.2, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + dur + 0.01);
  };
  beep(t, 440, 0.4); beep(t, 480, 0.4);
  beep(t + 0.55, 440, 0.4); beep(t + 0.55, 480, 0.4);
}

const PhoneIcon = MdPhone as any;
const CallEndIcon = MdCallEnd as any;
const PersonIcon = MdPerson as any;

interface IncomingCall {
  room_name: string;
  caller_id: number;
  caller_name: string;
  livekit_url: string;
}

interface Props {
  isAuthenticated: boolean;
  userName: string;
  accessToken?: string | null;
}

export const IncomingCallOverlay: React.FC<Props> = ({ isAuthenticated, userName, accessToken }) => {
  const { t } = useLanguage();
  const tc = (t as any).incomingCall;
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  const audioElRef  = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const ringIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Read pending call from IndexedDB (written by service worker on FCM arrival)
  const checkPendingCallFromIDB = useCallback(async () => {
    const data = await readPendingCall();
    if (!data?.room_name) return;
    await clearPendingCallIDB();
    setAccepting(false);
    setIncomingCall({
      room_name:   data.room_name,
      caller_id:   Number(data.caller_id),
      caller_name: data.caller_name,
      livekit_url: data.livekit_url || '',
    });
  }, []);

  const stopAllAudio = () => {
    if (audioElRef.current) { audioElRef.current.pause(); audioElRef.current = null; }
    if (ringIntervalRef.current) { clearInterval(ringIntervalRef.current); ringIntervalRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
  };

  const startBeepFallback = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      audioCtxRef.current = ctx;
      playBeepBurst(ctx);
      ringIntervalRef.current = setInterval(() => {
        if (audioCtxRef.current) playBeepBurst(audioCtxRef.current);
      }, 4000);
    } catch {}
  };

  // Ringtone while overlay is visible
  useEffect(() => {
    if (!incomingCall) { stopAllAudio(); return; }

    const file = getRingtoneFile();
    if (file) {
      const audio = new Audio(file);
      audio.loop = true;
      audio.volume = 0.85;
      audio.play().catch(() => {
        // File missing or blocked — fall back to beep
        audioElRef.current = null;
        startBeepFallback();
      });
      audioElRef.current = audio;
    } else {
      startBeepFallback();
    }

    return stopAllAudio;
  }, [incomingCall]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated) return;

    const socketUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';
    socketService.connect(socketUrl, accessToken || undefined);

    const handler = (e: Event) => {
      const data = (e as CustomEvent<IncomingCall>).detail;
      setAccepting(false);
      setAcceptError('');
      setIncomingCall(data);
    };
    const cancelHandler = () => {
      setIncomingCall(null);
      setAccepting(false);
      setAcceptError('');
    };
    window.addEventListener('socket:call:incoming', handler);
    window.addEventListener('socket:call:cancelled', cancelHandler);

    // Messages from service worker — app was minimized/closed when notification arrived
    const swMessageHandler = (e: MessageEvent) => {
      if (e.data?.type === 'call:incoming_from_notification') {
        const d = e.data.data;
        clearPendingCallIDB(); // IDB already read via SW, clear it
        setAccepting(false);
        setIncomingCall({
          room_name:   d.room_name,
          caller_id:   Number(d.caller_id),
          caller_name: d.caller_name,
          livekit_url: d.livekit_url,
        });
      }
      if (e.data?.type === 'call:decline_from_notification') {
        const d = e.data.data;
        // App was opened by SW just to send the reject — do it then close overlay
        axiosInstance.post('/calls/reject', {
          caller_id: Number(d.caller_id),
          room_name: d.room_name,
        }).catch(() => {});
        setIncomingCall(null);
        setAccepting(false);
      }
    };
    navigator.serviceWorker?.addEventListener('message', swMessageHandler);

    // When app returns to foreground (e.g. user taps FCM notification),
    // check IndexedDB for a pending call stored by the service worker.
    // This is the most reliable path for the "app minimized" scenario because
    // postMessage to a suspended WebView is not guaranteed to be delivered.
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkPendingCallFromIDB();
    };
    document.addEventListener('visibilitychange', onVisible);

    // Also check immediately on mount — covers cold-start from notification tap
    checkPendingCallFromIDB();

    return () => {
      window.removeEventListener('socket:call:incoming', handler);
      window.removeEventListener('socket:call:cancelled', cancelHandler);
      navigator.serviceWorker?.removeEventListener('message', swMessageHandler);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, accessToken, checkPendingCallFromIDB]);

  const handleAccept = useCallback(async () => {
    if (!incomingCall || accepting) return;
    setAcceptError('');
    // Minta izin mikrofon lewat plugin native dulu (sama jalur dengan Camera) sebelum
    // masuk ke CallScreen — supaya RECORD_AUDIO/AVAudioSession sudah granted di level
    // native, bukan baru diminta lewat WebView saat LiveKit memanggil getUserMedia.
    const micGranted = await ensureMicrophonePermission();
    if (!micGranted) {
      setAcceptError((tc as any)?.micPermissionDenied || 'Izin mikrofon diperlukan untuk menerima panggilan');
      return;
    }
    try {
      setAccepting(true);
      clearPendingCallIDB();
      const res = await axiosInstance.post('/calls/token', {
        room_name: incomingCall.room_name,
        participant_name: userName,
      });
      const { token } = res.data;
      sessionStorage.setItem(
        'livekit_call',
        JSON.stringify({
          token,
          room_name: incomingCall.room_name,
          livekit_url: incomingCall.livekit_url,
          caller_name: incomingCall.caller_name,
        })
      );
      setIncomingCall(null);
      window.location.href = '/CallScreen';
    } catch (err) {
      console.error('Failed to accept call:', err);
      setAccepting(false);
    }
  }, [incomingCall, accepting, userName]);

  const handleReject = useCallback(async () => {
    clearPendingCallIDB();
    if (incomingCall) {
      try {
        await axiosInstance.post('/calls/reject', {
          caller_id: incomingCall.caller_id,
          room_name: incomingCall.room_name,
        });
      } catch (err) {
        console.error('Failed to send reject notification:', err);
      }
    }
    setIncomingCall(null);
    setAccepting(false);
    setAcceptError('');
  }, [incomingCall]);

  if (!incomingCall) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
      }}
    >
      <div
        style={{
          backgroundColor: '#1F2937',
          borderRadius: 24,
          padding: '32px 24px',
          width: 280,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {/* Avatar ring pulse */}
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 36,
            backgroundColor: '#374151',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '3px solid #10B981',
          }}
        >
          <PersonIcon size={36} color="#9CA3AF" />
        </div>

        <p style={{ color: '#9CA3AF', fontSize: 12, margin: 0 }}>{tc?.title || 'Incoming Call'}</p>
        <p
          style={{
            color: '#FFFFFF',
            fontSize: 18,
            fontWeight: 700,
            margin: 0,
            textAlign: 'center',
          }}
        >
          {incomingCall.caller_name}
        </p>

        {accepting && (
          <p style={{ color: '#10B981', fontSize: 12, margin: 0 }}>{tc?.connecting || 'Connecting...'}</p>
        )}
        {acceptError && (
          <p style={{ color: '#F87171', fontSize: 12, margin: 0, textAlign: 'center' }}>{acceptError}</p>
        )}

        <div style={{ display: 'flex', gap: 40, marginTop: 12 }}>
          {/* Reject */}
          <button
            onClick={handleReject}
            disabled={accepting}
            style={{
              width: 60,
              height: 60,
              borderRadius: 30,
              backgroundColor: '#EF4444',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: accepting ? 'default' : 'pointer',
              opacity: accepting ? 0.5 : 1,
            }}
          >
            <CallEndIcon size={28} color="#fff" />
          </button>

          {/* Accept */}
          <button
            onClick={handleAccept}
            disabled={accepting}
            style={{
              width: 60,
              height: 60,
              borderRadius: 30,
              backgroundColor: '#10B981',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: accepting ? 'default' : 'pointer',
              opacity: accepting ? 0.7 : 1,
            }}
          >
            <PhoneIcon size={28} color="#fff" />
          </button>
        </div>
      </div>
    </div>
  );
};
